/**
 * @fileoverview Native Social Authentication Service
 *
 * Uses @capgo/capacitor-social-login for native Google Sign-In (iOS SDK)
 * and Apple Sign-In (ASAuthorizationController). No web redirects, no
 * custom URL schemes, no SFSafariViewController - just direct native auth.
 *
 * Flow:
 *  1. SocialLogin.login() → native UI → ID token
 *  2. supabase.auth.signInWithIdToken() → Supabase session
 *  3. onAuthStateChange fires → app is signed in
 */

import { SocialLogin } from '@capgo/capacitor-social-login';
import type { GoogleLoginOptions, GoogleLoginResponseOnline, AppleProviderResponse } from '@capgo/capacitor-social-login';
import { supabase } from '@/integrations/supabase/client';

// ─── Google Client IDs ────────────────────────────────────────────────────────
// iOS Client ID from Google Cloud Console (OAuth 2.0 → iOS type)
const GOOGLE_IOS_CLIENT_ID = '713512103524-tt75k36qoav1ge5jtk85hc40qbifu0nu.apps.googleusercontent.com';
// Web Client ID from Google Cloud Console (OAuth 2.0 → Web application type)
// Used by Supabase to validate the token audience.
const GOOGLE_WEB_CLIENT_ID = '713512103524-tfv987hfq0524kfijei3ep85ac6ed622.apps.googleusercontent.com';

// ─── Nonce Utilities ──────────────────────────────────────────────────────────

function getUrlSafeNonce(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function sha256Hash(message: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function getNonce(): Promise<{ rawNonce: string; nonceDigest: string }> {
  const rawNonce = getUrlSafeNonce();
  const nonceDigest = await sha256Hash(rawNonce);
  return { rawNonce, nonceDigest };
}

// ─── JWT Utilities ────────────────────────────────────────────────────────────

function decodeJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

// ─── Google Sign-In ───────────────────────────────────────────────────────────

/**
 * Native Google Sign-In for iOS using the Google Sign-In SDK.
 * Returns a Supabase session on success.
 *
 * @param retry - Internal flag for auto-retry on nonce cache issues
 */
export async function signInWithGoogleNative(retry = false): Promise<void> {
  const { rawNonce, nonceDigest } = await getNonce();

  await SocialLogin.initialize({
    google: {
      webClientId: GOOGLE_WEB_CLIENT_ID,
      iOSClientId: GOOGLE_IOS_CLIENT_ID,
      iOSServerClientId: GOOGLE_WEB_CLIENT_ID,
      mode: 'online',
    },
  });

  const response = await SocialLogin.login({
    provider: 'google',
    options: {
      scopes: ['email', 'profile'],
      nonce: nonceDigest,
    } as GoogleLoginOptions,
  });

  if (response.result.responseType !== 'online') {
    throw new Error('Google Sign-In offline mode is not supported');
  }

  const googleResponse = response.result as GoogleLoginResponseOnline;

  if (!googleResponse.idToken) {
    throw new Error('Google Sign-In did not return an ID token');
  }

  // iOS sometimes returns a cached token with the wrong nonce - auto-retry once
  const decoded = decodeJWT(googleResponse.idToken);
  if (decoded?.nonce && decoded.nonce !== nonceDigest) {
    if (!retry) {
      console.warn('[GoogleAuth] Nonce mismatch (cached token), logging out and retrying...');
      try { await SocialLogin.logout({ provider: 'google' }); } catch { /* ignore */ }
      return signInWithGoogleNative(true);
    }
    throw new Error('Google Sign-In nonce validation failed after retry');
  }

  // Sign in to Supabase with the native ID token
  const signInOptions: { provider: 'google'; token: string; nonce?: string } = {
    provider: 'google',
    token: googleResponse.idToken,
  };

  // Only pass nonce to Supabase if the token actually contains a nonce claim
  if (decoded?.nonce) {
    signInOptions.nonce = rawNonce;
  }

  const { error } = await supabase.auth.signInWithIdToken(signInOptions);
  if (error) throw error;
}

// ─── Apple Sign-In ────────────────────────────────────────────────────────────

/**
 * Native Apple Sign-In for iOS using ASAuthorizationController.
 * Returns a Supabase session on success.
 */
export async function signInWithAppleNative(): Promise<void> {

  await SocialLogin.initialize({
    apple: {},
  });

  const response = await SocialLogin.login({
    provider: 'apple',
    options: {},
  });

  const appleResponse = response.result as AppleProviderResponse;

  if (!appleResponse.idToken) {
    throw new Error('Apple Sign-In did not return an ID token');
  }

  const profile = appleResponse.profile;
  const fullName = [profile?.givenName, profile?.familyName].filter(Boolean).join(' ') || '';

  const { data, error: fnError } = await supabase.functions.invoke('apple-native-signin', {
    body: { idToken: appleResponse.idToken, fullName },
  });

  if (fnError || !data?.token_hash) {
    throw new Error(data?.error || 'Apple Sign-In failed');
  }

  const { error } = await supabase.auth.verifyOtp({
    token_hash: data.token_hash,
    type: 'magiclink',
  });

  if (error) throw error;
}
